import { useMemo, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { ArrowLeft, Download, Plus } from 'lucide-react'
import {
  Button,
  Chip,
  DataTable,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  PartnerPicker,
  Toolbar,
  ToolbarLeft,
} from '@/components/ui'
import type { PickerValue } from '@/components/ui/ProductPicker/ProductPicker'
import { letterPdfUrl, usePriceLetters } from '../api/letterQueries'
import type { LetterStatus, PriceLetter } from '../api/letterTypes'
import { NewPriceLetterDrawer } from '../components/NewPriceLetterDrawer'

const STATUS_FILTERS: { value: LetterStatus | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'finalized', label: 'Finalized' },
]

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const d = iso.length === 10 ? new Date(`${iso}T00:00:00`) : new Date(iso)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

/** History of price change letters, globally or for one customer (`?partner=`). */
export function PriceChangeLettersPage() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { partner?: string | number }
  const [partner, setPartner] = useState<PickerValue>(null)
  const [status, setStatus] = useState<LetterStatus | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const partnerId = partner?.[0] ?? (search.partner ? Number(search.partner) : null)
  const { data, isLoading, error, refetch } = usePriceLetters({ partnerId, status })
  const letters = useMemo(() => data?.letters ?? [], [data])

  const [cutoff] = useState(() => Date.now() - 30 * 86_400_000)
  const drafts = letters.filter((l) => l.status === 'draft').length
  const recent = letters.filter(
    (l) => l.finalized_at && new Date(l.finalized_at).getTime() >= cutoff,
  ).length

  const columns = useMemo<ColumnDef<PriceLetter, unknown>[]>(
    () => [
      {
        id: 'customer',
        header: 'Customer',
        cell: ({ row }) => <span className="text-role-data-primary">{row.original.partner_name}</span>,
      },
      { id: 'effective', header: 'Effective', cell: ({ row }) => fmtDate(row.original.effective_date) },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Chip size="xs" variant={row.original.status === 'finalized' ? 'success' : 'neutral'}>
            {row.original.status === 'finalized' ? 'Finalized' : 'Draft'}
          </Chip>
        ),
      },
      { id: 'lines', header: 'Lines', cell: ({ row }) => row.original.line_count ?? '—' },
      { id: 'by', header: 'Finalized by', cell: ({ row }) => row.original.finalized_by ?? '—' },
      { id: 'created', header: 'Created', cell: ({ row }) => fmtDate(row.original.created_at) },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) =>
          row.original.has_pdf ? (
            <a
              href={letterPdfUrl(row.original.id, true)}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-sm text-accent underline"
            >
              <Download size={14} /> PDF
            </a>
          ) : null,
      },
    ],
    [],
  )

  return (
    <div className="flex h-full flex-col">
      <PageHeaderBand
        title="Price change letters"
        description="Branded letters that go to customers with their revised pricing."
      >
        <MetricStrip variant="inline">
          <MetricCard variant="inline" label="Drafts" value={drafts} />
          <MetricCard variant="inline" label="Finalized (30 days)" value={recent} />
        </MetricStrip>
        <Button variant="secondary" size="sm" onClick={() => void navigate({ to: '/pricelists' } as never)}>
          <ArrowLeft size={14} /> Pricelists
        </Button>
        <Button variant="secondary" size="sm" onClick={() => void navigate({ to: '/pricelists/reviews' } as never)}>
          Price reviews
        </Button>
        <Button variant="primary" size="sm" onClick={() => setDrawerOpen(true)}>
          <Plus size={14} /> New letter
        </Button>
      </PageHeaderBand>

      <Toolbar>
        <ToolbarLeft>
          {STATUS_FILTERS.map((f) => (
            <Button
              key={f.label}
              size="sm"
              variant={status === f.value ? 'primary' : 'secondary'}
              onClick={() => setStatus(f.value)}
            >
              {f.label}
            </Button>
          ))}
          <div className="w-72">
            <PartnerPicker
              kind="customer"
              variant="field"
              placeholder="Filter by customer…"
              value={partner}
              onChange={setPartner}
            />
          </div>
        </ToolbarLeft>
      </Toolbar>

      <div className="min-h-0 flex-1 bg-canvas p-4">
        <DataTable
          moduleId="price-change-letters"
          data={letters}
          columns={columns}
          stickyFirstColumn={false}
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          errorSubject="price change letters"
          getRowId={(l) => l.id}
          onRowClick={(l) =>
            void navigate({ to: '/pricelists/letters/$letterId', params: { letterId: l.id } } as never)
          }
        />
      </div>

      <NewPriceLetterDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
    </div>
  )
}
